import { cargarDescartes } from "./alertasRecurrenciaDescarte";
import { normalizeMonthLocal } from "./cargarOportunidadesVentas";
import { agruparClientesPorEmpresa } from "./empresaContacto";
import { getDolarValue, loadExchangeRates } from "./exchangeRateService";
import { getClientes } from "./getClientes";
import { getContratos } from "./getContratos";
import { getOportunidades } from "./getOportunidades";
import { fetchMailSalienteResumen } from "./radarMailSaliente";

/**
 * Alertas de recurrencia comercial ("Radar Comercial") — docs/modulos/alertas-recurrencia-comercial.md.
 *
 * Criterio de REFERENCIA (sin cambios desde el rework 2026-09-25): una empresa (agrupada por
 * 10.276, nunca por fila de `clientes`) que el AÑO PASADO, ese mismo mes calendario (`mes_cierre`),
 * o bien CERRÓ (100%, "compró") o bien tuvo una propuesta que quedó en 0% ("se propuso y no
 * cerró") — nunca las dos juntas, gana el cierre. Es un ESTADO, no una ventana: se enciende 45 días
 * antes del 1° del mes objetivo y sigue encendido hasta que ese mes TERMINA (`mesesActivos`).
 *
 * Fase A del rediseño (2026-09-26): la función pasa de FILTRAR las alertas apagadas a devolver
 * TODAS las candidatas de los meses encendidos, cada una con `estado` y `motivoEstado`:
 *   - `cerrada`: propuesta abierta (50/75/90) o cierre (100%) este año en ese mes.
 *   - `escrita`: mail SALIENTE (nunca entrante — ver `radarMailSaliente.js`) a un contacto de la
 *     empresa, fecha ≥ encendido. Ya NO apaga una reunión o llamada sincronizada (antes sí, vía
 *     `crm_actividad_resumen_grupos`, que mezclaba cualquier tipo) — decisión de Ignacio: solo
 *     mail o cotización apagan un aviso.
 *   - `postergada`: fila en `alertas_recurrencia_descartadas` con `posponer_hasta >= hoy`.
 *   - `vencida`: ninguna de las anteriores y ya pasaron más de `DIAS_VENCIDA` desde el encendido.
 *   - `pendiente`: el resto.
 * El header/panel siguen contando y mostrando solo `pendiente`+`vencida` (ver
 * AlertasRecurrenciaBanner) — `cerrada`/`escrita`/`postergada` quedan disponibles en el objeto para
 * la página de gestión de una fase futura, no para el panel del header.
 *
 * Cada alerta lleva además `clientId`/`salesId`/`esContrato` de la VENTA DE REFERENCIA (la más
 * reciente del grupo en ese mes del año pasado — 100% gana sobre 0% si hubiera ambas) y, de esa
 * misma venta, `campana`, `mesCierre`, `ano` (año de exhibición/consumo, 10.252 — NO el año en que
 * cerró), `edificiosTotales`, `edificiosParticipantes`. Sin `importe` (decisión de Ignacio, no va
 * al mail).
 *
 * Alcance: `sales` + `sales_contratos` (10.127); `sales_btl` FUERA. Uruguay fuera (10.181/10.267).
 * Conversión de moneda (10.185, TC del día 15 del mes de cierre) y agrupación por empresa (10.276)
 * sin cambios.
 */

export const MESES_ES = [
  "",
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

const ESTADOS_ABIERTOS = ["50%", "75%", "90%"];
const DIAS_ANTICIPACION = 45;
const DIAS_VENCIDA = 14;

/**
 * Carga `sales` + `sales_contratos` + `clientes` de un país (oportunidades SIN filtrar por
 * estatus) y, con los datos ya en memoria, el mail saliente atribuible (`fetchMailSalienteResumen`,
 * consulta acotada nueva — ver ese archivo) y los descartes/postergaciones manuales. Ya NO trae la
 * actividad de CRM precalculada (`fetchResumenGrupos`): apagaba por CUALQUIER tipo de actividad
 * (MEET/CALL/MAIL, sin distinguir dirección) — retirado en la Fase A del rediseño (2026-09-26,
 * decisión de Ignacio: solo mail SALIENTE o cotización apagan). Loader LIVIANO a propósito (no
 * reusa el orquestador pesado `cargarOportunidadesVentas`).
 */
export const cargarDatosAlertasRecurrencia = async ({ pais }) => {
  const [oportunidadesData, contratosData, clientesData] = await Promise.all([
    getOportunidades({ pais }),
    getContratos({ pais }),
    getClientes({ pais }),
    loadExchangeRates(),
  ]);

  const oportunidades = (oportunidadesData || [])
    .filter((o) => !o.eliminada)
    .map((o) => ({
      id: o.id,
      esContrato: false,
      client_id: o.client_id,
      estatus: o.estatus || "0%",
      importe: o.importe,
      moneda_dolar: o.moneda_dolar,
      created_at: o.created_at,
      campana: o.campana,
      ano: o.ano,
      edificiosTotales: o.edificiostotales,
      edificiosParticipantes: o.edificiosparticipantes,
      mes_cierre: normalizeMonthLocal(o.mes_cierre, o.ano || o.ano_cierre, o.created_at, o.mes_cierre_old || o.mes_old),
    }));

  const contratos = (contratosData || [])
    .filter((c) => !c.eliminada)
    .map((c) => ({
      id: c.id,
      esContrato: true,
      client_id: c.client_id,
      estatus: c.estatus || "50%",
      importe: c.importe_total,
      moneda_dolar: c.moneda_dolar,
      created_at: c.created_at,
      campana: c.campana,
      ano: c.ano,
      edificiosTotales: c.edificios_referencia_totales,
      edificiosParticipantes: c.edificios_referencia,
      mes_cierre: normalizeMonthLocal(c.mes_cierre, c.ano, c.created_at, c.mes_inicio),
    }));

  const clientes = clientesData || [];

  const [descartes, mailSalienteResumen] = await Promise.all([
    cargarDescartes({ pais }),
    fetchMailSalienteResumen({ pais, clientes }),
  ]);

  return {
    oportunidades: [...oportunidades, ...contratos],
    clientes,
    descartes,
    mailSalienteResumen,
  };
};

/**
 * Meses actualmente "encendidos": aquellos cuyo 1° cae dentro de los próximos `DIAS_ANTICIPACION`
 * días desde `hoy`, o cuyo mes todavía no terminó. Puede devolver 0, 1, 2 o 3 meses.
 */
const mesesActivos = (hoy) => {
  const base = new Date(hoy);
  base.setHours(0, 0, 0, 0);
  const targets = [];
  for (let n = -3; n <= 3; n++) {
    const day1 = new Date(base.getFullYear(), base.getMonth() + n, 1);
    const ultimoDia = new Date(base.getFullYear(), base.getMonth() + n + 1, 0);
    const activacion = new Date(day1);
    activacion.setDate(activacion.getDate() - DIAS_ANTICIPACION);
    if (base >= activacion && base <= ultimoDia) {
      targets.push({ mes: day1.getMonth() + 1, ano: day1.getFullYear(), activacion });
    }
  }
  return targets;
};

const formatDiaMes = (fecha) => {
  const d = new Date(fecha);
  return `${d.getDate()}/${d.getMonth() + 1}`;
};

/**
 * Calcula TODAS las alertas de recurrencia candidatas de los meses encendidos, con `estado` y
 * `motivoEstado` (Fase A, 2026-09-26 — antes filtraba las apagadas). Función pura, testeable.
 *
 * @returns {Array<{tipo, empresaNorm, empresaDisplay, mesObjetivo, anoObjetivo, anoPasado, monto,
 *   veces, estado, motivoEstado, clientId, salesId, esContrato, campana, mesCierre, ano,
 *   edificiosTotales, edificiosParticipantes}>} ordenado por `monto` descendente.
 */
export const calcularAlertasRecurrencia = ({
  oportunidades,
  clientes,
  descartes = null,
  mailSalienteResumen = [],
  getDolarValueFn = getDolarValue,
  hoy = new Date(),
  pais,
}) => {
  const gruposEmpresa = agruparClientesPorEmpresa(clientes);
  const empresaPorClienteId = new Map();
  for (const [, grupo] of gruposEmpresa) {
    for (const clienteId of grupo.clienteIds) empresaPorClienteId.set(String(clienteId), grupo.empresaNorm);
  }

  const ultimoMailSalientePorEmpresa = new Map(mailSalienteResumen.map((m) => [m.empresa_norm, m.ultima_fecha]));

  // (d) postergación VIGENTE — comparación por fecha local (no UTC, mismo criterio que
  // `mesesActivos`). Se guarda el valor de `posponer_hasta` (no solo un booleano) para el texto.
  const hoyLocalISO = (() => {
    const d = new Date(hoy);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const posponerHastaPorClave = new Map(
    (descartes || [])
      .filter((d) => d.posponer_hasta && d.posponer_hasta >= hoyLocalISO)
      .map((d) => [`${d.empresa_norm}|${d.mes}|${d.ano}`, d.posponer_hasta])
  );

  const sumas = new Map(); // `${empresaNorm}|${mes}|${ano}|${'100'|'0'}` -> { suma, veces, refVenta }
  const abiertasPorGrupo = new Set(); // `${empresaNorm}|${mes}|${ano}` con 50/75/90 apuntando ahí

  for (const o of oportunidades) {
    const empresaNorm = empresaPorClienteId.get(String(o.client_id));
    if (!empresaNorm || !o.mes_cierre) continue;
    const [mesStr, anoStr] = o.mes_cierre.split("-");
    const mes = parseInt(mesStr, 10);
    const ano = parseInt(anoStr, 10);
    if (!mes || !ano) continue;

    if (o.estatus === "100%" || o.estatus === "0%") {
      const esDolar = o.moneda_dolar === true;
      const importe = Number(o.importe) || 0;
      const rate = esDolar ? getDolarValueFn(`${anoStr}-${mesStr}-15`, pais) || 1 : 1;
      const montoLocal = esDolar ? importe * rate : importe;
      const key = `${empresaNorm}|${mes}|${ano}|${o.estatus === "100%" ? "100" : "0"}`;
      const acc = sumas.get(key) || { suma: 0, veces: 0, refVenta: null };
      acc.suma += montoLocal;
      acc.veces += 1;
      if (!acc.refVenta || o.created_at > acc.refVenta.created_at) acc.refVenta = o;
      sumas.set(key, acc);
    } else if (ESTADOS_ABIERTOS.includes(o.estatus)) {
      abiertasPorGrupo.add(`${empresaNorm}|${mes}|${ano}`);
    }
  }

  const targets = mesesActivos(hoy);
  const alertas = [];
  for (const t of targets) {
    const anoPasado = t.ano - 1;
    for (const [empresaNorm, grupo] of gruposEmpresa) {
      const sum100 = sumas.get(`${empresaNorm}|${t.mes}|${anoPasado}|100`);
      const sum0 = sumas.get(`${empresaNorm}|${t.mes}|${anoPasado}|0`);
      let tipo;
      let base;
      if (sum100) {
        tipo = "compro";
        base = sum100;
      } else if (sum0) {
        tipo = "propuso";
        base = sum0;
      } else {
        continue;
      }

      const keyEsteAno = `${empresaNorm}|${t.mes}|${t.ano}`;
      const cerroEsteAno = sumas.has(`${keyEsteAno}|100`);
      const abiertaEsteAno = abiertasPorGrupo.has(keyEsteAno);
      const ultimoMail = ultimoMailSalientePorEmpresa.get(empresaNorm);
      const escrita = !!ultimoMail && new Date(ultimoMail) >= t.activacion;
      const posponerHasta = posponerHastaPorClave.get(keyEsteAno);

      const alertaBase = {
        tipo,
        empresaNorm,
        empresaDisplay: grupo.empresaDisplay,
        mesObjetivo: t.mes,
        anoObjetivo: t.ano,
        anoPasado,
        monto: Math.round(base.suma),
        veces: base.veces,
      };

      let estado;
      let motivoEstado;
      if (cerroEsteAno || abiertaEsteAno) {
        estado = "cerrada";
        motivoEstado = cerroEsteAno ? "Cerró este año" : "Propuesta abierta";
      } else if (escrita) {
        estado = "escrita";
        motivoEstado = `Mail enviado el ${formatDiaMes(ultimoMail)}`;
      } else if (posponerHasta) {
        estado = "postergada";
        motivoEstado = `Postergada hasta el ${formatDiaMes(posponerHasta)}`;
      } else {
        const diasDesdeEncendido = Math.floor((hoy - t.activacion) / 86400000);
        estado = diasDesdeEncendido > DIAS_VENCIDA ? "vencida" : "pendiente";
        motivoEstado = motivoAlerta(alertaBase);
      }

      const ref = base.refVenta;
      alertas.push({
        ...alertaBase,
        estado,
        motivoEstado,
        clientId: ref?.client_id ?? null,
        salesId: ref?.id ?? null,
        esContrato: ref?.esContrato ?? false,
        campana: ref?.campana ?? null,
        mesCierre: ref?.mes_cierre ?? null,
        ano: ref?.ano ?? null,
        edificiosTotales: ref?.edificiosTotales ?? null,
        edificiosParticipantes: ref?.edificiosParticipantes ?? null,
      });
    }
  }

  return alertas.sort((a, b) => b.monto - a.monto);
};

/**
 * Motivo de referencia ("Compró en octubre del año pasado" / "Se propuso en octubre y no
 * cerró") — se usa como `motivoEstado` de las alertas `pendiente`/`vencida` (las otras 3 llevan su
 * propio texto de estado, ver `calcularAlertasRecurrencia`). Texto neutro a propósito ("Se
 * propuso", no "Le propusiste"): el aviso lo ve todo el equipo.
 */
export const motivoAlerta = (alerta) => {
  const mes = MESES_ES[alerta.mesObjetivo];
  const sufijo = alerta.veces > 1 ? ` (${alerta.veces}${alerta.tipo === "compro" ? " ventas" : " propuestas"})` : "";
  return alerta.tipo === "compro"
    ? `Compró en ${mes} del año pasado${sufijo}`
    : `Se propuso en ${mes} y no cerró${sufijo}`;
};

/**
 * Texto de estado para la columna de acciones de quien SOLO VE el Radar (`puedeActuarRadar` en
 * falso) — reusa `motivoEstado` ya calculado arriba, nunca reimplementa el formato de fecha.
 * `postergada` no entra hoy al panel (filtro `pendiente`/`vencida` de AlertasRecurrenciaBanner),
 * pero la función queda correcta por si ese filtro se amplía.
 */
export const textoEstadoRadar = (alerta) => (alerta.estado === "postergada" ? alerta.motivoEstado : "Pendiente");

/**
 * Formato compacto ($180M / $45K) para el total del encabezado del panel — la suma de los montos
 * YA convertidos (10.185), sin volver a tocar la conversión. No hay un formateador compacto
 * centralizado en el proyecto (10.27); acotado a esta única línea, las filas siguen con
 * `formatAmount`/`useCurrency` completo (10.1).
 */
export const formatMontoCompacto = (monto) => {
  const abs = Math.abs(monto);
  if (abs >= 1_000_000) return `$ ${Math.round(monto / 1_000_000)}M`;
  if (abs >= 1_000) return `$ ${Math.round(monto / 1_000)}K`;
  return `$ ${Math.round(monto)}`;
};
